/**
 * SSE parsing utilities using fetch + ReadableStream.
 *
 * We use fetch (instead of EventSource) because we need:
 *  - Custom headers (JWT Authorization via Bearer token)
 *  - POST requests (EventSource only supports GET)
 *  - Fine-grained stream lifecycle control (reader lock/release)
 */

// Parse a single SSE line, returning the parsed JSON event or null
export function parseSseLine(line) {
  if (!line.startsWith("data: ")) return null;
  const data = line.slice(6).trim();
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

// Async generator that yields parsed SSE events from a fetch Response
export async function* readSseStream(response) {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("Response body not readable");

  try {
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        const event = parseSseLine(line);
        if (event) yield event;
      }
    }
    // Process any remaining data in the buffer
    const event = parseSseLine(buffer.trim());
    if (event) yield event;
  } finally {
    reader.releaseLock();
  }
}
